import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSelector } from "react-redux";

import MySnackbar from "../../components/UI/MySnackBar";
import { DeleteConfirmationModal } from "../../components/UI/Tables/Modals/deleteConfirmationModal";
import { RootCeleste } from "../../components/Root";
import { HomeViewTitle } from "../../styles/Titles";
import {
  addAdmin,
  deleteAdmin,
  demoteToTutor,
  editAdmin,
  getAdmins,
  getPromotableTutors,
  promoteToAdmin,
} from "../../api/handleAdmins";

const Root = RootCeleste;
const Title = HomeViewTitle;

// Igual que el minimo que valida el backend (admins/schemas.py)
const MIN_PASSWORD_LENGTH = 8;

const EMPTY_FORM = { name: "", last_name: "", email: "", password: "" };

const AdminsView = () => {
  const user = useSelector((state) => state.user);

  const [admins, setAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  // null => el modal esta cerrado; sin id => alta; con id => edicion
  const [formTarget, setFormTarget] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [promotables, setPromotables] = useState([]);
  const [openPromoteModal, setOpenPromoteModal] = useState(false);
  const [tutorToPromote, setTutorToPromote] = useState("");

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    status: "",
  });

  const notify = (message, status) =>
    setNotification({ open: true, message, status });

  const handleSnackbarClose = () =>
    setNotification({ ...notification, open: false });

  const fetchAdmins = useCallback(async () => {
    try {
      setIsLoading(true);
      const [fetchedAdmins, fetchedTutors] = await Promise.all([
        getAdmins(user),
        getPromotableTutors(user),
      ]);
      setAdmins(fetchedAdmins);
      setPromotables(fetchedTutors);
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const isEditing = Boolean(formTarget?.id);

  const openCreateModal = () => {
    setForm(EMPTY_FORM);
    setFormTarget({});
  };

  const openEditModal = (admin) => {
    setForm({
      name: admin.name,
      last_name: admin.last_name,
      email: admin.email,
      password: "",
    });
    setFormTarget(admin);
  };

  const closeFormModal = () => {
    setFormTarget(null);
    setForm(EMPTY_FORM);
  };

  // En el alta la contraseña es obligatoria; en la edicion, vacia significa
  // "dejar la que ya tiene".
  const passwordIsValid =
    isEditing
      ? form.password === "" || form.password.length >= MIN_PASSWORD_LENGTH
      : form.password.length >= MIN_PASSWORD_LENGTH;

  const formIsValid =
    form.name.trim() &&
    form.last_name.trim() &&
    form.email.trim() &&
    passwordIsValid;

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      if (isEditing) {
        await editAdmin(formTarget.id, form, user);
        notify("Administrador actualizado exitosamente", "success");
      } else {
        await addAdmin(form, user);
        notify("Administrador creado exitosamente", "success");
      }
      closeFormModal();
      await fetchAdmins();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (adminId, closeModal) => {
    try {
      await deleteAdmin(adminId, user);
      notify("Administrador eliminado exitosamente", "success");
      closeModal();
      await fetchAdmins();
    } catch (error) {
      notify(error.message, "error");
      closeModal();
    }
  };

  const handlePromote = async () => {
    try {
      setIsSaving(true);
      await promoteToAdmin(tutorToPromote, user);
      notify("Tutor promovido a administrador", "success");
      closePromoteModal();
      await fetchAdmins();
    } catch (error) {
      notify(error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDemote = async (admin) => {
    try {
      await demoteToTutor(admin.id, user);
      notify(`${admin.name} ${admin.last_name} volvió a ser tutor`, "success");
      await fetchAdmins();
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const closePromoteModal = () => {
    setOpenPromoteModal(false);
    setTutorToPromote("");
  };

  const openDeleteConfirmation = (admin) => {
    setAdminToDelete(admin);
    setOpenDeleteModal(true);
  };

  // El backend rechaza ambos casos; deshabilitar el boton evita el ida y vuelta.
  const deleteBlockedReason = (admin) => {
    if (admin.id === user.id) return "No podés eliminar tu propia cuenta";
    if (admins.length <= 1) return "No se puede eliminar el último administrador";
    return null;
  };

  // Mismas guardas que el borrado: sin admins nadie puede volver a crear uno.
  const demoteBlockedReason = (admin) => {
    if (admin.id === user.id) return "No podés bajarte el rol a vos mismo";
    if (admins.length <= 1) return "No se puede bajar el último administrador";
    return null;
  };

  return (
    <Root maxWidth="md">
      <Title variant="h4">Administradores</Title>
      <Typography variant="body1" style={{ color: "#555", marginBottom: 16 }}>
        Las cuentas de administrador tienen acceso completo al sistema. Los
        estudiantes y tutores se cargan por cuatrimestre desde su CSV.
      </Typography>

      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Apellido</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Mail</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin) => {
                const blockedReason = deleteBlockedReason(admin);
                const demoteReason = demoteBlockedReason(admin);
                return (
                  <TableRow key={admin.id}>
                    <TableCell>{admin.name}</TableCell>
                    <TableCell>{admin.last_name}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton
                          color="primary"
                          onClick={() => openEditModal(admin)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={demoteReason || "Bajar a tutor"}>
                        <span>
                          <IconButton
                            color="warning"
                            disabled={Boolean(demoteReason)}
                            onClick={() => handleDemote(admin)}
                          >
                            <ArrowDownwardIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={blockedReason || "Eliminar"}>
                        <span>
                          <IconButton
                            color="error"
                            disabled={Boolean(blockedReason)}
                            onClick={() => openDeleteConfirmation(admin)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "center",
          marginTop: 3,
        }}
      >
        <Button
          onClick={openCreateModal}
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
        >
          Agregar administrador
        </Button>
        <Button
          onClick={() => setOpenPromoteModal(true)}
          color="primary"
          variant="outlined"
          disabled={promotables.length === 0}
        >
          Sumar un tutor como admin
        </Button>
      </Box>

      <Dialog
        open={Boolean(formTarget)}
        onClose={closeFormModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? "Editar administrador" : "Agregar administrador"}
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <DialogContent dividers>
            <TextField
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Apellido"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label={isEditing ? "Nueva contraseña" : "Contraseña"}
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              fullWidth
              margin="normal"
              required={!isEditing}
              error={form.password !== "" && !passwordIsValid}
              helperText={
                isEditing
                  ? `Dejala vacía para no cambiarla. Mínimo ${MIN_PASSWORD_LENGTH} caracteres.`
                  : `Mínimo ${MIN_PASSWORD_LENGTH} caracteres. Comunicásela a la persona por fuera del sistema.`
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeFormModal} variant="outlined" color="primary">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!formIsValid || isSaving}
            >
              {isEditing ? "Guardar" : "Agregar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openPromoteModal}
        onClose={closePromoteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sumar un tutor como administrador</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handlePromote();
          }}
        >
          <DialogContent dividers>
            <Typography variant="body2" style={{ color: "#555" }}>
              Le cambia el rol al mismo usuario: conserva su contraseña y los
              cuatrimestres que tutorea. Va a poder alternar entre la vista de
              admin y la de tutor desde el menú de su perfil.
            </Typography>
            <TextField
              select
              label="Tutor"
              value={tutorToPromote}
              onChange={(e) => setTutorToPromote(e.target.value)}
              fullWidth
              margin="normal"
              required
            >
              {promotables.map((tutor) => (
                <MenuItem key={tutor.id} value={tutor.id}>
                  {tutor.last_name}, {tutor.name} ({tutor.email})
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={closePromoteModal}
              variant="outlined"
              color="primary"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!tutorToPromote || isSaving}
            >
              Promover
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <DeleteConfirmationModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        handleDelete={handleDelete}
        item={adminToDelete}
        setParentItem={setAdminToDelete}
        itemTypeName={
          adminToDelete
            ? `al administrador ${adminToDelete.name} ${adminToDelete.last_name}`
            : "el administrador"
        }
      />

      <MySnackbar
        open={notification.open}
        handleClose={handleSnackbarClose}
        message={notification.message}
        status={notification.status}
      />
    </Root>
  );
};

export default AdminsView;
